import isEqual from 'lodash-es/isEqual';
import { create } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { lccstate, userstate, rollupStatus, assessmentChange, posttestChange, practiceChange, assessmentScore, statusChange } from 'lcc-state';
import { numeral } from '@holmescorp/numeral';
import { quizstate } from '../support/state';
import { recordComplete } from '../support/xapi';
const icache = createICacheMiddleware();
const factory = create({ icache, quizstate, userstate, lccstate, theme }).properties();
const isEmpty = (obj) => obj !== undefined && obj !== null && Object.keys(obj).length === 0;
export const ScoreRollup = factory(({ properties, children, middleware: { icache, quizstate, userstate, lccstate } }) => {
    const { quizstore, includeParents = true } = properties();
    quizstate.setStore(quizstore);
    const _last = icache.getOrSet('last', {});
    const state_data = () => {
        const { get, path } = quizstate;
        const { get: uget, path: upath } = userstate;
        const { get: lget, path: lpath } = lccstate;
        return {
            quiz: get(path('quiz')),
            info: get(path('info')),
            status: uget(upath('status')),
            tracking: lget(lpath('tracking'))
        };
    };
    const pre_post_scores = (lastresult) => {
        const { bymodule = {}, bylevel1 = {} } = lastresult;
        const scores = {};
        Object.keys(bymodule).forEach((name) => {
            const toname = name.toLowerCase();
            const module = bymodule[name];
            scores[toname] = {
                parent: null,
                previous: null,
                next: null,
                data: {
                    pointscorrect: module.correct,
                    pointsincorrect: module.total - module.correct,
                    percent: Number(module.percent),
                    complete: 0,
                    total: 0,
                    readtime: 0
                }
            };
        });
        Object.keys(bylevel1).forEach((refname) => {
            const [name, ref] = refname.split('.');
            const toname = name.toLowerCase();
            const tgf = ref.includes('topic') ? ref : `tgf${ref}`;
            const module = bylevel1[refname];
            const parent = toname;
            scores[tgf] = {
                parent,
                previous: null,
                next: null,
                data: {
                    pointscorrect: module.correct,
                    pointsincorrect: module.total - module.correct,
                    percent: Number(module.percent),
                    complete: 0,
                    total: 0,
                    readtime: 0
                }
            };
        });
        return scores;
    };
    const check_rollup = () => {
        const { quiz, info, status, tracking } = state_data();
        const lastresult = info && info.lastresult;
        const quizkind = quiz && quiz.kind;
        if (!['pretest', 'posttest', 'practice'].includes(quizkind) && status === undefined) {
            return;
        }
        if (quiz && /bonus|discussion|casestudy/.test(quiz.id)) {
            return;
        }
        if (quiz && lastresult && !isEmpty(lastresult)) {
            if (!isEqual(lastresult, _last)) {
                icache.set('last', lastresult);
                if (['pretest', 'posttest', 'practice'].includes(quizkind)) {
                    const scores = pre_post_scores(lastresult);
                    recordComplete(lastresult, scores, quiz);
                    if (quizkind === 'pretest') {
                        const percent = lastresult.totalpercentcorrect * 0.01;
                        assessmentScore({ score: `${numeral(percent).format('0%')}` });
                        assessmentChange({ assessment: scores });
                    }
                    else if (quizkind === 'practice') {
                        practiceChange({ practice: scores });
                    }
                    else {
                        const { percentcorrect } = lastresult;
                        const score = Number(percentcorrect);
                        const posttest = {
                            tree: scores,
                            passed: score >= 80,
                            score
                        };
                        posttestChange({ posttest });
                    }
                }
                else {
                    const { topicset = '', topicgroup = '' } = tracking ? tracking : {};
                    const chkForQuiz = new RegExp('quiz|((calculation|exercise)-\\d+)');
                    const isTopicset = chkForQuiz.test(topicgroup);
                    const chkForActivity = new RegExp('((calculation|exercise)-\\d+)');
                    const isActivity = chkForActivity.test(topicgroup);
                    const topickey = isActivity ? `${topicset}-${topicgroup}` : isTopicset ? topicset : topicgroup;
                    const data = { ...status[topickey]?.data };
                    const { totalpercentcorrect, numbercorrect, numberincorrect } = lastresult;
                    const calcPercent = totalpercentcorrect * 0.01;
                    data.percent = calcPercent;
                    data.pointscorrect = numbercorrect;
                    data.pointsincorrect = numberincorrect;
                    if (includeParents) {
                        rollupStatus(topickey, data);
                    }
                    else if (topickey in status) {
                        status[topickey].data = { ...status[topickey].data, ...data };
                        statusChange({ status });
                    }
                    else {
                        status[topickey] = {
                            data: {
                                ...data,
                                readtime: 0,
                                total: 0
                            },
                            next: null,
                            parent: topicset,
                            previous: null
                        };
                        statusChange({ status });
                    }
                    recordComplete(lastresult, pre_post_scores(lastresult), quiz);
                }
            }
        }
    };
    check_rollup();
    return children();
});
